import { useEffect } from 'react';
import { X } from 'lucide-react';
import WeeklyCardBack from './WeeklyCardBack';
import { WeekWithCards } from '../lib/weeklyCardsPlanner';

interface PublishedEditionModalProps {
  edition: WeekWithCards;
  onClose: () => void;
}

export default function PublishedEditionModal({ edition, onClose }: PublishedEditionModalProps) {
  const artistWisdom = edition.cards.find(c => c.card_type === 'artist_wisdom');
  const civicMoment = edition.cards.find(c => c.card_type === 'civic_moment');
  const sparkThought = edition.cards.find(c => c.card_type === 'spark_a_thought');

  const publishedTimestamp = edition.week.published_at
    ? new Date(edition.week.published_at).toLocaleString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : 'Unknown date';

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#0F0F0F] border border-[#282828] rounded-2xl w-full max-w-[1100px] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-8 py-6 border-b border-[#282828] flex items-center justify-between">
          <div>
            <h2 className="text-[22px] font-semibold text-[#F5F5F5] mb-1">Published Edition</h2>
            <p className="text-[13px] text-[#808080]">Published {publishedTimestamp}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-[#808080] hover:text-[#F5F5F5] hover:bg-[#282828] rounded-lg transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body - Three Cards */}
        <div className="px-8 py-10 pb-12 bg-[#0A0A0A]">
          <div className="flex flex-row items-center justify-center gap-6">
            {/* Artist Wisdom Card */}
            <div
              className="origin-top-left"
              style={{
                width: '300px',
                height: '400px',
                transform: 'scale(0.833)',
              }}
            >
              <div
                className="w-[360px] h-[480px] bg-bg-card rounded-[20px] border border-stroke flex flex-col"
                style={{
                  boxShadow:
                    '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)',
                }}
              >
                {artistWisdom?.content ? (
                  <WeeklyCardBack
                    cardType="artist_wisdom"
                    imageUrl={artistWisdom.image_url}
                    content={artistWisdom.content}
                    attribution={artistWisdom.attribution}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-[13px] text-[#606060] text-center">Not set</p>
                  </div>
                )}
              </div>
            </div>

            {/* Civic Moment Card */}
            <div
              className="origin-top-left"
              style={{
                width: '300px',
                height: '400px',
                transform: 'scale(0.833)',
              }}
            >
              <div
                className="w-[360px] h-[480px] bg-bg-card rounded-[20px] border border-stroke flex flex-col"
                style={{
                  boxShadow:
                    '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)',
                }}
              >
                {civicMoment?.content ? (
                  <WeeklyCardBack
                    cardType="civic_moment"
                    imageUrl={civicMoment.country?.image_url}
                    content={civicMoment.content}
                    title={civicMoment.title}
                    countryName={civicMoment.country?.name}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-[13px] text-[#606060] text-center">Not set</p>
                  </div>
                )}
              </div>
            </div>

            {/* Spark a Thought Card */}
            <div
              className="origin-top-left"
              style={{
                width: '300px',
                height: '400px',
                transform: 'scale(0.833)',
              }}
            >
              <div
                className="w-[360px] h-[480px] bg-bg-card rounded-[20px] border border-stroke flex flex-col"
                style={{
                  boxShadow:
                    '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)',
                }}
              >
                {sparkThought?.content ? (
                  <WeeklyCardBack
                    cardType="civic_spark"
                    imageUrl={null}
                    content={sparkThought.content}
                    subtext={sparkThought.subtext}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-[13px] text-[#606060] text-center">Not set</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
